/*
| Week presets — fill an empty week from a saved template (or by copying another
| week), and bulk-assign a person to a workplace's open shifts. Thin vanilla TS
| over the planning template API; reuses pl-modal, combobox and toast. The
| empty-state hero reacts to the calendar's `planning:week-rendered` event so it
| tracks AJAX week navigation.
*/

import { enhanceSelect } from './combobox';
import { toast } from './toast';
import { screenSignal } from './screenScope';

const CHECK_SVG =
    '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3.5 8.5l3 3 6-7"/></svg>';

interface Named {
    id: number;
    name: string;
}

interface WeekPresetsConfig {
    routes: Record<string, string>;
    workplaces: Named[];
    employees: Named[];
    i18n: Record<string, string>;
}

const axios = (): any => (window as any).axios;

export function initWeekTemplates(): void {
    const island = document.querySelector<HTMLElement>('[data-week-presets]');
    if (!island) {
        return;
    }
    const cfg: WeekPresetsConfig = JSON.parse(island.textContent || '{}');
    const grid = document.querySelector<HTMLElement>('.pl-grid');
    const saveBtn = document.querySelector<HTMLButtonElement>('[data-wp-save]');
    // Week-only dropdown actions — disabled in month view (no single target week).
    const weekOnlyItems = [
        document.querySelector<HTMLButtonElement>('[data-wp-fill]'),
        document.querySelector<HTMLButtonElement>('[data-wp-assign]'),
    ];

    let weekStart = '';
    let hero: HTMLElement | null = null;

    document.addEventListener(
        'planning:week-rendered',
        ((e: CustomEvent) => {
            weekStart = e.detail.weekStart;
            const isWeekView = (e.detail.view ?? 'week') === 'week';
            const empty = e.detail.count === 0;
            // Empty-week hero + "save week" make sense only in the week grid; in month
            // view the calendar owns visibility (don't re-show the week grid → no stacking).
            saveBtn?.toggleAttribute('hidden', empty || !isWeekView);
            weekOnlyItems.forEach((btn) => {
                btn?.toggleAttribute('disabled', !isWeekView);
                btn?.setAttribute('aria-disabled', String(!isWeekView));
            });
            if (!isWeekView) {
                hero?.remove();
                hero = null;
                return;
            }
            toggleHero(empty);
        }) as EventListener,
        { signal: screenSignal() },
    );

    // "Šablony ▾" dropdown — save / fill / manage grouped under one trigger.
    const menuWrap = document.querySelector<HTMLElement>('.wp-menu-wrap');
    const menuTrigger = document.querySelector<HTMLButtonElement>('[data-wp-menu]');
    const menuPop = document.querySelector<HTMLElement>('[data-wp-menu-pop]');
    // Relocate the popover to <body> so it escapes `.screen`'s animation transform
    // (a transformed ancestor is the containing block even for position:fixed, so
    // z-index alone can't lift it above the grid otherwise).
    if (menuPop) {
        document.body.appendChild(menuPop);
    }
    const closeMenu = (): void => {
        if (!menuPop || menuPop.hidden) {
            return;
        }
        menuPop.hidden = true;
        menuTrigger?.setAttribute('aria-expanded', 'false');
        window.removeEventListener('scroll', closeMenu, true);
        window.removeEventListener('resize', closeMenu);
    };
    const openMenu = (): void => {
        if (!menuPop || !menuTrigger) {
            return;
        }
        menuPop.hidden = false; // show first so offset dimensions are measurable
        const rect = menuTrigger.getBoundingClientRect();
        const w = menuPop.offsetWidth;
        const left = Math.max(8, Math.min(rect.right - w, window.innerWidth - w - 8));
        menuPop.style.left = `${left}px`;
        menuPop.style.top = `${rect.bottom + 6}px`;
        menuTrigger.setAttribute('aria-expanded', 'true');
        window.addEventListener('scroll', closeMenu, true);
        window.addEventListener('resize', closeMenu);
    };
    menuTrigger?.addEventListener('click', (e) => {
        e.stopPropagation();
        if (menuPop?.hidden) {
            openMenu();
        } else {
            closeMenu();
        }
    });
    document.addEventListener(
        'click',
        (e) => {
            const t = e.target as Node;
            if (menuWrap && !menuWrap.contains(t) && menuPop && !menuPop.contains(t)) {
                closeMenu();
            }
        },
        { signal: screenSignal() },
    );
    document.addEventListener(
        'keydown',
        (e) => {
            if (e.key === 'Escape') {
                closeMenu();
            }
        },
        { signal: screenSignal() },
    );

    saveBtn?.addEventListener('click', () => {
        closeMenu();
        save();
    });
    document.querySelector('[data-wp-fill]')?.addEventListener('click', () => {
        closeMenu();
        void openFill();
    });
    document.querySelector('[data-wp-manage]')?.addEventListener('click', () => {
        closeMenu();
        void openManage();
    });
    document.querySelector('[data-wp-assign]')?.addEventListener('click', () => {
        closeMenu();
        void openAssign();
    });

    // Shortcut from a workplace row of the grid — same dialog, narrowed to that room.
    document.addEventListener(
        'planning:assign-row',
        ((e: CustomEvent) => {
            weekStart = e.detail?.weekStart ?? weekStart;
            void openAssign(e.detail?.workplaceId);
        }) as EventListener,
        { signal: screenSignal() },
    );

    function toggleHero(empty: boolean): void {
        if (!empty) {
            hero?.remove();
            hero = null;
            if (grid) {
                grid.hidden = false;
            }
            return;
        }
        if (hero || !grid) {
            return;
        }
        if (grid) {
            grid.hidden = true;
        }
        hero = buildHero();
        grid.parentElement?.insertBefore(hero, grid);
    }

    function buildHero(): HTMLElement {
        const el = document.createElement('div');
        el.className = 'pl-card wp-hero';
        el.innerHTML =
            `<div class="wp-hero-title">${esc(cfg.i18n.emptyTitle)}</div>` +
            `<p class="pl-muted">${esc(cfg.i18n.emptyLead)}</p>` +
            '<div class="wp-hero-list" data-wp-list></div>' +
            `<button type="button" class="pl-btn pl-btn-ghost" data-wp-copy>${esc(cfg.i18n.copy)}</button>`;
        el.querySelector('[data-wp-copy]')?.addEventListener('click', () => void copyWeek());
        void loadTemplates(el.querySelector<HTMLElement>('[data-wp-list]')!, (id) => void preview(id));
        return el;
    }

    async function loadTemplates(into: HTMLElement, onPick: (id: number) => void): Promise<Named[]> {
        const { data } = await axios().get(cfg.routes.index);
        const templates: Named[] = data.templates ?? [];
        into.innerHTML = '';
        if (templates.length === 0) {
            into.innerHTML = `<span class="pl-muted">${esc(cfg.i18n.manageEmpty)}</span>`;
        }
        templates.forEach((t) => {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'pl-btn pl-btn-ghost wp-template';
            btn.textContent = `${cfg.i18n.apply}: ${t.name}`;
            btn.addEventListener('click', () => onPick(t.id));
            into.appendChild(btn);
        });
        return templates;
    }

    function save(): void {
        const input = document.createElement('input');
        input.type = 'text';
        input.className = 'wp-input';
        input.placeholder = cfg.i18n.savePrompt;
        const people = document.createElement('label');
        people.className = 'pl-check wp-save-people';
        people.innerHTML =
            `<input type="checkbox" data-wp-include-workers><span class="pl-check__box">${CHECK_SVG}</span>` +
            `<span class="pl-check__label">${esc(cfg.i18n.saveIncludeWorkers)}</span>`;
        const includeWorkers = people.querySelector<HTMLInputElement>('input')!;
        const wrap = document.createElement('div');
        wrap.appendChild(input);
        wrap.appendChild(people);
        modal(
            cfg.i18n.saveTitle,
            '',
            cfg.i18n.saveConfirm,
            () => {
                const name = input.value.trim();
                if (!name) {
                    input.focus();
                    return false;
                }
                void (async () => {
                    try {
                        await axios().post(cfg.routes.store, {
                            name,
                            week_start: weekStart,
                            include_workers: includeWorkers.checked,
                        });
                        toast(cfg.i18n.saved);
                    } catch (err: any) {
                        toast(err?.response?.data?.error ?? cfg.i18n.errEmptyWeek);
                    }
                })();
                return true;
            },
            wrap,
        );
    }

    // "Naplnit týden ze šablony…" — pick a saved template to apply to the current week.
    async function openFill(): Promise<void> {
        const list = document.createElement('div');
        list.className = 'wp-manage-list';
        await loadTemplates(list, (id) => void preview(id));
        modal(cfg.i18n.fillTitle, '', null, () => true, list);
    }

    async function preview(templateId: number): Promise<void> {
        const { data } = await axios().post(cfg.routes.preview, { template_id: templateId, week_start: weekStart });
        if (data.kind === 'blocked') {
            toast(blockedMessage(data.reason));
            return;
        }
        openPreviewModal(data, () => void apply(templateId));
    }

    async function apply(templateId: number): Promise<void> {
        const { data } = await axios().post(cfg.routes.apply, { template_id: templateId, week_start: weekStart });
        if (data.kind === 'blocked') {
            toast(blockedMessage(data.reason));
            return;
        }
        // Re-render the current week in place — week navigation is JS-only, so a full
        // reload would drop back to the default week. The grid + hero react to the refetch.
        document.dispatchEvent(new CustomEvent('planning:refresh-shifts'));
        toast(cfg.i18n.filled);
    }

    function copyWeek(): void {
        const input = document.createElement('input');
        input.type = 'date';
        input.className = 'wp-input';
        const wrap = document.createElement('div');
        wrap.appendChild(input);
        modal(
            cfg.i18n.copyTitle,
            '',
            cfg.i18n.confirm,
            () => {
                const source = input.value;
                if (!source) {
                    input.focus();
                    return false;
                }
                void (async () => {
                    const { data } = await axios().post(cfg.routes.copy, {
                        source_week_start: source,
                        target_week_start: weekStart,
                    });
                    if (data.kind === 'blocked') {
                        toast(blockedMessage(data.reason));
                        return;
                    }
                    document.dispatchEvent(new CustomEvent('planning:refresh-shifts'));
                    toast(cfg.i18n.filled);
                })();
                return true;
            },
            wrap,
        );
    }

    function openPreviewModal(data: any, onConfirm: () => void): void {
        const rows = (data.rows ?? [])
            .map(
                (r: any) =>
                    `<li><span class="ag-row-main"><span class="ag-row-label">${esc(r.date)}</span>` +
                    (r.workplace ? `<span class="ag-row-sub">${esc(r.workplace)}</span>` : '') +
                    (r.worker ? `<span class="ag-row-sub wp-preview-worker">${esc(r.worker)}</span>` : '') +
                    `</span><span class="ag-row-val">${esc(r.time)}</span></li>`,
            )
            .join('');
        const warns = (data.warnings ?? [])
            .map(
                (w: any) =>
                    `<div class="ap-warn"><span class="pill pill--warn">!</span><span>${esc(w.message)}</span></div>`,
            )
            .join('');
        const body = `<ul class="wp-preview-list">${rows}</ul>${warns ? `<div class="ap-warnings">${warns}</div>` : ''}`;
        const title = cfg.i18n.previewTitle.replace(':count', String(data.count ?? 0));
        modal(title, body, cfg.i18n.confirm, () => {
            onConfirm();
            return true;
        });
    }

    async function openManage(): Promise<void> {
        const list = document.createElement('div');
        list.className = 'wp-manage-list';
        const refresh = async (): Promise<void> => {
            const { data } = await axios().get(cfg.routes.index);
            const templates: Named[] = data.templates ?? [];
            list.innerHTML = templates.length ? '' : `<span class="pl-muted">${esc(cfg.i18n.manageEmpty)}</span>`;
            templates.forEach((t: any) => {
                const row = document.createElement('div');
                row.className = 'wp-manage-row';
                row.innerHTML = `<span>${esc(t.name)}</span><span class="pl-muted">${cfg.i18n.manageCount.replace(':count', String(t.count))}</span>`;
                const del = document.createElement('button');
                del.type = 'button';
                del.className = 'pl-btn pl-btn-ghost';
                del.textContent = cfg.i18n.manageDelete;
                del.addEventListener('click', async () => {
                    await axios().delete(`${cfg.routes.destroyBase}/${t.id}`);
                    void refresh();
                });
                row.appendChild(del);
                list.appendChild(row);
            });
        };
        await refresh();
        modal(cfg.i18n.manageTitle, '', null, () => true, list);
    }

    async function openAssign(onlyWorkplaceId?: number): Promise<void> {
        // Best-effort per-workplace count of OPEN (unassigned) shifts this week, so
        // the manager sees what there is to fill before picking a person.
        const openCounts: Record<number, number> = {};
        try {
            const { data } = await axios().get(cfg.routes.range, {
                params: { from: weekStart, to: weekEnd(weekStart), open: 1 },
            });
            (data.shifts ?? []).forEach((s: any) => {
                if (s.workplace_id) {
                    openCounts[s.workplace_id] = (openCounts[s.workplace_id] ?? 0) + 1;
                }
            });
        } catch {
            // counts are advisory; the assign action still works without them
        }

        const wrap = document.createElement('div');
        wrap.className = 'wp-assign-list';
        const intro = document.createElement('p');
        intro.className = 'pl-muted wp-assign-intro';
        intro.textContent = cfg.i18n.assignIntro.replace(':week', weekRangeLabel());
        wrap.appendChild(intro);

        const targets = onlyWorkplaceId ? cfg.workplaces.filter((wp) => wp.id === onlyWorkplaceId) : cfg.workplaces;

        targets.forEach((wp) => {
            const count = openCounts[wp.id] ?? 0;
            const row = document.createElement('div');
            row.className = 'wp-assign-row';
            const name = document.createElement('span');
            name.className = 'wp-assign-name';
            name.innerHTML = `${esc(wp.name)} <span class="pl-muted wp-assign-count">${esc(cfg.i18n.assignOpenCount.replace(':count', String(count)))}</span>`;
            row.appendChild(name);

            if (count === 0) {
                const none = document.createElement('span');
                none.className = 'pl-muted wp-assign-empty';
                none.textContent = cfg.i18n.assignNone;
                row.appendChild(none);
                wrap.appendChild(row);
                return;
            }

            const control = document.createElement('div');
            control.className = 'wp-assign-control';
            const select = document.createElement('select');
            select.id = `wp-assign-${wp.id}`;
            // Real <option> elements so enhanceSelect's `select.value = v` works.
            const placeholderOption = document.createElement('option');
            placeholderOption.value = '';
            placeholderOption.textContent = cfg.i18n.assignPlaceholder;
            select.appendChild(placeholderOption);
            cfg.employees.forEach((e) => {
                const opt = document.createElement('option');
                opt.value = String(e.id);
                opt.textContent = e.name;
                select.appendChild(opt);
            });
            control.appendChild(select);
            const handle = enhanceSelect(select, { placeholder: cfg.i18n.assignPlaceholder });

            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'pl-btn pl-btn-primary wp-assign-go';
            btn.textContent = cfg.i18n.assignButton;
            btn.disabled = true;
            select.addEventListener('change', () => {
                btn.disabled = !select.value;
            });
            btn.addEventListener('click', async () => {
                btn.disabled = true;
                try {
                    const { data } = await axios().post(cfg.routes.assignRow, {
                        workplace_id: wp.id,
                        week_start: weekStart,
                        user_id: Number(select.value),
                    });
                    handle.destroy();
                    const result = document.createElement('span');
                    result.className = 'wp-assign-result';
                    result.textContent = cfg.i18n.assignDone
                        .replace(':assigned', String(data.assigned))
                        .replace(':skipped', String(data.skipped));
                    control.replaceChildren(result);
                } catch (err: any) {
                    btn.disabled = false;
                    toast(err?.response?.data?.error ?? cfg.i18n.errEmptyWeek);
                }
            });
            control.appendChild(btn);
            row.appendChild(control);
            wrap.appendChild(row);
            handle.refresh();
        });

        modal(
            cfg.i18n.assignTitle,
            '',
            null,
            () => true,
            wrap,
            () => window.location.reload(),
        );
    }

    function weekEnd(start: string): string {
        // Noon + local getters avoid the UTC/DST shift that toISOString() introduces.
        const d = new Date(`${start}T12:00:00`);
        d.setDate(d.getDate() + 6);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    }

    function weekRangeLabel(): string {
        const cz = (iso: string): string => {
            const [, m, d] = iso.split('-');
            return `${Number(d)}.${Number(m)}.`;
        };
        return `${cz(weekStart)} – ${cz(weekEnd(weekStart))}`;
    }

    function blockedMessage(reason: string): string {
        if (reason === 'not_empty') {
            return cfg.i18n.errNotEmpty;
        }
        if (reason === 'closed_month') {
            return cfg.i18n.errClosed;
        }
        return cfg.i18n.errEmptyWeek;
    }

    function modal(
        title: string,
        bodyHtml: string,
        confirmLabel: string | null,
        onConfirm: () => boolean,
        bodyEl?: HTMLElement,
        onClose?: () => void,
    ): void {
        const backdrop = document.createElement('div');
        backdrop.className = 'pl-modal-backdrop';
        const panel = document.createElement('div');
        panel.className = 'pl-modal wp-modal';
        panel.setAttribute('role', 'dialog');
        panel.setAttribute('aria-modal', 'true');
        panel.innerHTML = `<h2 class="wp-modal-title">${esc(title)}</h2><div class="wp-modal-body">${bodyHtml}</div>`;
        if (bodyEl) {
            panel.querySelector('.wp-modal-body')!.appendChild(bodyEl);
        }
        const actions = document.createElement('div');
        actions.className = 'wp-modal-actions';
        const close = (): void => {
            backdrop.remove();
            onClose?.();
        };
        if (confirmLabel) {
            const ok = document.createElement('button');
            ok.type = 'button';
            ok.className = 'btn-cta';
            ok.textContent = confirmLabel;
            ok.addEventListener('click', () => {
                if (onConfirm()) {
                    close();
                }
            });
            actions.appendChild(ok);
        }
        const cancel = document.createElement('button');
        cancel.type = 'button';
        cancel.className = 'pl-btn pl-btn-ghost';
        cancel.textContent = cfg.i18n.cancel;
        cancel.addEventListener('click', close);
        actions.appendChild(cancel);
        panel.appendChild(actions);
        backdrop.appendChild(panel);
        backdrop.addEventListener('click', (e) => {
            if (e.target === backdrop) {
                close();
            }
        });
        document.addEventListener('keydown', function onKey(e) {
            if (e.key === 'Escape') {
                close();
                document.removeEventListener('keydown', onKey);
            }
        });
        document.body.appendChild(backdrop);
        panel.querySelector<HTMLElement>('input, textarea, button, select, [tabindex]')?.focus();
    }
}

function esc(value: string): string {
    const div = document.createElement('div');
    div.textContent = value ?? '';
    return div.innerHTML;
}
